/**
 * Küçük hash yönlendirici. Ayrı bir yönlendirme kütüphanesi eklemiyoruz:
 * sekiz sabit ekran var, hash yolu servis işçisiyle (tek kabuk) çakışmaz ve
 * statik barındırmada sunucu yapılandırması gerektirmez.
 */

import { useEffect, useState } from "react";
import { M } from "../dil";

export const ROUTES = [
  "ana-sayfa",
  "hesaplar",
  "hareketler",
  "ice-aktar",
  "pozisyonlar",
  "rapor",
  "grafik",
  "donemsel-analiz",
  "sanal-hesaplar",
  "izleme",
  "fiyatlar",
  "degerleme",
  "ayarlar",
] as const;

export type Route = (typeof ROUTES)[number];

export const ROUTE_TITLE: Readonly<Record<Route, string>> = M.kabuk.sayfa;

/** Ana sayfa: defter açılınca durumuna göre şekil alan kartlar (10.10.2026). */
export const DEFAULT_ROUTE: Route = "ana-sayfa";

export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  return (ROUTES as readonly string[]).includes(raw) ? (raw as Route) : DEFAULT_ROUTE;
}

export function routeHref(route: Route): string {
  return `#/${route}`;
}

export function useRoute(): [Route, (route: Route) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(globalThis.location?.hash ?? ""));

  useEffect(() => {
    const onHashChange = (): void => setRoute(parseRoute(globalThis.location?.hash ?? ""));
    globalThis.addEventListener?.("hashchange", onHashChange);
    return () => globalThis.removeEventListener?.("hashchange", onHashChange);
  }, []);

  const navigate = (next: Route): void => {
    if (globalThis.location) globalThis.location.hash = routeHref(next);
    else setRoute(next);
  };

  return [route, navigate];
}

/**
 * Yolun "?" sonrası — bir ekrana tek seferlik istek taşır (ör. açılışta
 * "giriş formu açık": `#/hareketler?yeni=1`; uyarıdan Ayarlar'ın bölümüne:
 * `#/ayarlar?bolum=depolama`). Yol seçimi bunu yok sayar (`parseRoute`).
 */
export function routeSorgusu(hash: string = globalThis.location?.hash ?? ""): URLSearchParams {
  const i = hash.indexOf("?");
  return new URLSearchParams(i < 0 ? "" : hash.slice(i + 1));
}

/**
 * İsteği bir kez okur ve adresten siler (geçmişe yeni kayıt eklemeden):
 * sayfa yeniden yüklenince ya da geri gelinince form yeniden açılmasın.
 */
export function routeSorgusunuAl(anahtar: string): string | null {
  const deger = routeSorgusu().get(anahtar);
  if (deger === null) return null;
  const hash = globalThis.location?.hash ?? "";
  const yalin = hash.split("?")[0] ?? "";
  try {
    globalThis.history?.replaceState(globalThis.history.state, "", yalin === "" ? " " : yalin);
  } catch {
    // replaceState yoksa (ör. test ortamı) istek adreste kalır; zararsız.
  }
  return deger;
}
